import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Smartphone, Copy, Check, ExternalLink, KeyRound, FileSignature, Star, Settings2, Eye } from 'lucide-react';
import { useBookings, useActiveProperty, type Booking } from '../../../hooks/useManagement';
import PropertySelector from '../../../components/PropertySelector';

const GUEST_APP_BASE = 'https://guest.kanarix.es/g/';

type GuestBooking = Booking & {
  guest_token?: string | null;
  agreement_signed_at?: string | null;
  review_submitted?: number | null;
};

/**
 * Owner view of the interactive guest app: one row per stay with the shareable
 * link and the guest's progress (check-in → signed agreement → review), plus a
 * per-property preview and a shortcut to the guest-guide content fields.
 */
export default function ManageGuestAppPage() {
  const { t } = useTranslation();
  const { propertyId, properties, allSelected } = useActiveProperty();
  const { data: bookings, isLoading } = useBookings('all', propertyId);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const rows = useMemo(
    () => ((bookings ?? []) as GuestBooking[])
      .filter(b => (b.status === 'confirmed' || b.status === 'completed') && b.guest_token),
    [bookings],
  );

  const copy = (b: GuestBooking) => {
    navigator.clipboard?.writeText(`${GUEST_APP_BASE}${b.guest_token}`).then(() => {
      setCopiedId(b.id);
      setTimeout(() => setCopiedId(null), 1800);
    }).catch(() => {});
  };

  // Property-level preview links (the app works with a property token too).
  const activeProps = allSelected ? properties : properties.filter(p => p.id === propertyId);

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Smartphone size={22} className="text-teal-600" />
            {t('portal.manage.guestApp.title', 'App del huésped')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('portal.manage.guestApp.subtitle', 'Check-in online, contrato digital, guía de bienvenida y contacto directo — un enlace por reserva.')}
          </p>
        </div>
        <PropertySelector allowAll />
      </div>

      {/* Per-property: preview + content shortcut */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">
          {t('portal.manage.guestApp.propertiesTitle', 'Vista previa y contenido por alojamiento')}
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {activeProps.map(p => (
            <div key={p.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3.5 py-2.5">
              <span className="truncate text-sm font-medium text-slate-800">{p.name || `#${p.id}`}</span>
              <span className="flex items-center gap-1.5 shrink-0">
                {p.guest_token && (
                  <a
                    href={`${GUEST_APP_BASE}${p.guest_token}`}
                    target="_blank" rel="noreferrer"
                    title={t('portal.manage.guestApp.preview', 'Vista previa')}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    <Eye size={13} /> {t('portal.manage.guestApp.preview', 'Vista previa')}
                  </a>
                )}
                <Link
                  to={`/portal/manage/properties?edit=${p.id}`}
                  title={t('portal.manage.guestApp.editContent', 'Editar contenido (WiFi, llaves, guía)')}
                  className="inline-flex items-center gap-1 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-xs font-semibold text-teal-700 hover:bg-teal-100"
                >
                  <Settings2 size={13} /> {t('portal.manage.guestApp.editContent', 'Contenido')}
                </Link>
              </span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-400">
          {t('portal.manage.guestApp.contentHint', 'El contenido de la app (WiFi, código de llaves, instrucciones de llegada, manual de la casa) se edita en cada alojamiento.')}
        </p>
      </div>

      {/* Per-booking links + progress */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.bookings.colGuest', 'Huésped')}</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.bookings.colDates', 'Fechas')}</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.guestApp.colProgress', 'Progreso')}</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-right">{t('portal.manage.guestApp.colLink', 'Enlace')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading && (
                <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">…</td></tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">
                  {t('portal.manage.guestApp.empty', 'No hay reservas confirmadas con app del huésped todavía.')}
                </td></tr>
              )}
              {rows.map(b => (
                <tr key={b.id} className="hover:bg-slate-50/60">
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900">{b.guest_name}</div>
                    <div className="text-xs text-slate-400">{b.code}{allSelected && b.property_name ? ` · ${b.property_name}` : ''}</div>
                  </td>
                  <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{b.check_in} → {b.check_out}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <FlowDot done={!!b.registration_completed_at} icon={<KeyRound size={12} />} label={t('portal.manage.guestApp.stepCheckin', 'Check-in')} />
                      <FlowDot done={!!b.agreement_signed_at} icon={<FileSignature size={12} />} label={t('portal.manage.guestApp.stepAgreement', 'Contrato')} />
                      <FlowDot done={!!b.review_submitted} icon={<Star size={12} />} label={t('portal.manage.guestApp.stepReview', 'Reseña')} />
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => copy(b)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        {copiedId === b.id ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                        {copiedId === b.id ? t('portal.mySite.copied', 'Copiado') : t('portal.mySite.copy', 'Copiar')}
                      </button>
                      <a
                        href={`${GUEST_APP_BASE}${b.guest_token}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-lg bg-teal-600 hover:bg-teal-700 px-2.5 py-1.5 text-xs font-semibold text-white"
                      >
                        <ExternalLink size={13} /> {t('portal.mySite.open', 'Abrir')}
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FlowDot({ done, icon, label }: { done: boolean; icon: React.ReactNode; label: string }) {
  return (
    <span
      title={label}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border ${
        done ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'
      }`}
    >
      {icon} {label}
    </span>
  );
}
